import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FitnessCenter as FitnessIcon,
  Restaurant as RestaurantIcon,
  Scale as ScaleIcon,
  LocalFireDepartment as FireIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import api from '../../../services/api';
import { grey, primary, success, warning, info } from '../../../styles/tokens';
import { translateIntolerances, translateDietaryPreferences } from '../../../utils/translate';
import { calculateBMR, calculateDEJ, getPeriodLabel } from '../../../utils/calculateMacros';

// Map NAP number to label
const getNAPLabelFromNumber = (nap) => {
  const labels = {
    1.3: 'Sedentaire',
    1.4: 'Sedentaire',
    1.5: 'Legerement actif',
    1.6: 'Moderement actif',
    1.7: 'Actif',
    1.8: 'Tres actif',
  };
  return labels[nap] || 'Moderement actif';
};

/**
 * Detail d'un Client (Coach)
 */
const ClientView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // V2.0: Recalcul des macros
  const [recalculateLoading, setRecalculateLoading] = useState(false);
  const [recalculateSuccess, setRecalculateSuccess] = useState(null);

  // Changement de statut client
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    fetchClient();
  }, [id]);

  const fetchClient = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.get(`/coach/clients/${id}`);
      setClient(data);
    } catch (err) {
      console.error('Erreur lors du chargement du client:', err);
      setError('Impossible de charger les details du client.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      await api.delete(`/coach/clients/${id}`);
      setDeleteDialog(false);
      navigate('/coach/clients');
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError('Impossible de supprimer le client.');
    } finally {
      setActionLoading(false);
    }
  };

  // V2.0: Recalculer les macros du client
  const handleRecalculateMacros = async () => {
    try {
      setRecalculateLoading(true);
      setError(null);
      await api.post(`/coach/clients/${id}/recalculate-macros`, {
        applyToWeekPlans: false
      });
      setRecalculateSuccess('Besoins nutritionnels recalcules avec succes !');
      // Rafraichir les donnees client
      await fetchClient();
    } catch (err) {
      console.error('Erreur lors du recalcul:', err);
      setError(err.response?.data?.message || 'Erreur lors du recalcul des macros.');
    } finally {
      setRecalculateLoading(false);
    }
  };

  // Toggle statut client (actif/inactif)
  const handleToggleStatus = async () => {
    try {
      setStatusLoading(true);
      setError(null);
      const newStatus = client.status === 'active' ? 'inactive' : 'active';
      await api.put(`/coach/clients/${id}`, { status: newStatus });
      setClient(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error('Erreur lors du changement de statut:', err);
      setError(err.response?.data?.message || 'Erreur lors du changement de statut.');
    } finally {
      setStatusLoading(false);
    }
  };

  // Calculs
  const bmr = client ? calculateBMR(client) : null;
  const nap = client?.sportProfile?.nap || 1.5;
  const dej = bmr ? calculateDEJ(bmr, nap) : null;
  const period = client?.sportProfile?.period || 'base';

  // Genre
  const getGenderLabel = (gender) => {
    const labels = { male: 'Homme', female: 'Femme', other: 'Autre' };
    return labels[gender] || 'Non renseigne';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error && !client) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
          onClick={() => navigate('/coach/clients')}
          sx={{ mb: 3 }}
        >
          Retour
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{client?.firstName} {client?.lastName} - Coach Yoco</title>
      </Helmet>

      <Box>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate('/coach/clients')}>
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Typography variant="h1">
            Detail du client
          </Typography>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {recalculateSuccess && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setRecalculateSuccess(null)}>
            {recalculateSuccess}
          </Alert>
        )}

        {/* V2.0: Alerte macros obsoletes */}
        {client?.macrosOutOfDate && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleRecalculateMacros}
                disabled={recalculateLoading}
              >
                {recalculateLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                Recalculer
              </Button>
            }
          >
            Les donnees physiques ont ete modifiees. Les besoins nutritionnels doivent etre recalcules.
          </Alert>
        )}

        {/* Profile Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              alignItems={{ xs: 'center', sm: 'center' }}
              textAlign={{ xs: 'center', sm: 'left' }}
            >
              <Avatar
                sx={{
                  width: { xs: 72, sm: 80 },
                  height: { xs: 72, sm: 80 },
                  bgcolor: primary[100],
                  color: primary[600],
                  fontSize: '1.5rem',
                  fontWeight: 600,
                }}
              >
                {client?.firstName?.[0]}{client?.lastName?.[0]}
              </Avatar>

              <Box sx={{ flex: 1 }}>
                <Typography variant="h2" sx={{ mb: 1 }}>
                  {client?.firstName} {client?.lastName}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mb: 2 }} justifyContent={{ xs: 'center', sm: 'flex-start' }} flexWrap="wrap">
                  <Chip
                    label={client?.status === 'active' ? 'Actif' : 'Inactif'}
                    size="small"
                    icon={client?.status === 'active' ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : <CancelIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      bgcolor: client?.status === 'active' ? success[50] : grey[100],
                      color: client?.status === 'active' ? success.dark : grey[600],
                      '& .MuiChip-icon': {
                        color: client?.status === 'active' ? success.main : grey[400],
                      },
                    }}
                    onClick={handleToggleStatus}
                    disabled={statusLoading}
                    clickable
                  />
                  <Chip
                    label={getGenderLabel(client?.gender)}
                    size="small"
                    sx={{ bgcolor: grey[100], color: grey[700] }}
                  />
                  {client?.age && (
                    <Chip
                      label={`${client.age} ans`}
                      size="small"
                      sx={{ bgcolor: info[50], color: info.dark }}
                    />
                  )}
                </Stack>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1, sm: 3 }}
                  alignItems={{ xs: 'center', sm: 'flex-start' }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <EmailIcon sx={{ fontSize: 18, color: grey[400] }} />
                    <Typography variant="body2">{client?.email}</Typography>
                  </Stack>
                  {client?.phone && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PhoneIcon sx={{ fontSize: 18, color: grey[400] }} />
                      <Typography variant="body2">{client?.phone}</Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>

              {/* Actions */}
              <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' }, mt: { xs: 2, sm: 0 } }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon sx={{ fontSize: 18 }} />}
                  onClick={() => navigate(`/coach/clients/edit/${id}`)}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  Modifier
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
                  onClick={() => setDeleteDialog(true)}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  Supprimer
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Stats Cards - Grid 2x2 on mobile, 4 cols on desktop */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          <Card>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar sx={{ bgcolor: primary[50], width: { xs: 36, sm: 40 }, height: { xs: 36, sm: 40 } }}>
                  <ScaleIcon sx={{ color: primary[500], fontSize: { xs: 18, sm: 20 } }} />
                </Avatar>
                <Box>
                  <Typography variant="h2" sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}>
                    {client?.weight ? `${client.weight} kg` : '-'}
                  </Typography>
                  <Typography variant="caption">Poids</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar sx={{ bgcolor: success[50], width: { xs: 36, sm: 40 }, height: { xs: 36, sm: 40 } }}>
                  <FitnessIcon sx={{ color: success.main, fontSize: { xs: 18, sm: 20 } }} />
                </Avatar>
                <Box>
                  <Typography variant="h2" sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}>
                    {client?.height ? `${client.height} cm` : '-'}
                  </Typography>
                  <Typography variant="caption">Taille</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar sx={{ bgcolor: warning[50], width: { xs: 36, sm: 40 }, height: { xs: 36, sm: 40 } }}>
                  <FireIcon sx={{ color: warning.main, fontSize: { xs: 18, sm: 20 } }} />
                </Avatar>
                <Box>
                  <Typography variant="h2" sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}>
                    {bmr ? `${bmr}` : '-'}
                  </Typography>
                  <Typography variant="caption">BMR (kcal/j)</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar sx={{ bgcolor: info[50], width: { xs: 36, sm: 40 }, height: { xs: 36, sm: 40 } }}>
                  <RestaurantIcon sx={{ color: info.main, fontSize: { xs: 18, sm: 20 } }} />
                </Avatar>
                <Box>
                  <Typography variant="h2" sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}>
                    {dej ? `${dej}` : '-'}
                  </Typography>
                  <Typography variant="caption">DEJ (kcal/j)</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Profil Sportif */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h3" sx={{ mb: 2 }}>
              Profil sportif
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Niveau d'activite (NAP)</Typography>
                <Typography variant="body1">{nap} - {getNAPLabelFromNumber(nap)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Periode d'entrainement</Typography>
                <Typography variant="body1">{getPeriodLabel(period)}</Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Objectifs et Sante */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h3" sx={{ mb: 2 }}>
              Objectifs et sante
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Objectifs</Typography>
                <Typography variant="body1">{client?.goals || 'Non renseigne'}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Conditions medicales</Typography>
                <Typography variant="body1">
                  {client?.medicalConditions?.length ? client.medicalConditions.join(', ') : 'Aucune'}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Intolerances alimentaires</Typography>
                <Typography variant="body1">
                  {client?.intolerances?.length ? translateIntolerances(client.intolerances).join(', ') : 'Aucune'}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Regime Alimentaire */}
        <Card>
          <CardContent>
            <Typography variant="h3" sx={{ mb: 2 }}>
              Regime alimentaire
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Preferences alimentaires</Typography>
                <Typography variant="body1">
                  {client?.dietaryPreferences?.length
                    ? translateDietaryPreferences(client.dietaryPreferences).join(', ')
                    : 'Aucune'}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Dialog Suppression */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
          <DialogTitle>Supprimer le client ?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Etes-vous sur de vouloir supprimer <strong>{client?.firstName} {client?.lastName}</strong> ?
              <br /><br />
              <strong>Cette action est irreversible.</strong> Toutes les donnees du client seront perdues.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)} variant="outlined">
              Annuler
            </Button>
            <Button onClick={handleDelete} color="error" variant="contained" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default ClientView;
